import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../AuthProvider';
import { getApiToken } from '../authUtils';
import { differenceInDays } from 'date-fns';

import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TablePagination,
  InputAdornment,
  IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

const urlconfig = require('../config/urlconfig');

const SampleDashboard = () => {
    const { account } = useAuth();
    const { instance } = useMsal();
    const [samples, setSamples] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
    const [remarks, setRemarks] = useState({});

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setPage(0);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchSamples = useCallback(async (search, currentPage, currentRowsPerPage) => {
        if (!window.__E2E_TEST__ && !account) return;
        setLoading(true);
        try {
            const accessToken = await getApiToken(instance, account);
            const response = await fetch(`${urlconfig.backend_api_base_url}/api/v2/samples?search=${encodeURIComponent(search)}&page=${currentPage}&size=${currentRowsPerPage}`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            if (!response.ok) {
                throw new Error('Failed to fetch samples');
            }
            const data = await response.json();

            setSamples(data.content || []);
            setTotalElements(data.totalElements || 0);

            const initialRemarks = {};
            (data.content || []).forEach(sample => {
                initialRemarks[sample.id] = sample.remarks || '';
            });
            setRemarks(initialRemarks);

        } catch (error) {
            console.error("Failed to fetch samples:", error);
        } finally {
            setLoading(false);
        }
    }, [instance, account]);

    useEffect(() => {
        fetchSamples(debouncedSearchQuery, page, rowsPerPage);
    }, [debouncedSearchQuery, page, rowsPerPage, fetchSamples]);

    const updateSample = async (sampleId, payload) => {
        try {
            const accessToken = await getApiToken(instance, account);
            await fetch(`${urlconfig.backend_api_base_url}/api/v2/samples/${sampleId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
                body: JSON.stringify(payload)
            });
            fetchSamples(debouncedSearchQuery, page, rowsPerPage);
        } catch (error) {
            console.error("Failed to update sample:", error);
        }
    };

    const handleStatusChange = (sampleId, newStatus) => {
        const sample = samples.find(s => s.id === sampleId);
        updateSample(sampleId, { ...sample, sampleStatus: newStatus });
    };

    const handleRemarksChange = (sampleId, value) => {
        setRemarks(prev => ({ ...prev, [sampleId]: value }));
    };

    const handleRemarksBlur = (sampleId) => {
        const sample = samples.find(s => s.id === sampleId);
        updateSample(sampleId, { ...sample, remarks: remarks[sampleId] });
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
                Samples Dashboard
            </Typography>

            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Search Samples"
                    placeholder="Client Name, Details, Sample ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setSearchQuery('')} size="small">
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sample ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Client Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>In Current Status (Days)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Remarks</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : samples.length > 0 ? (
                            samples.map(sample => (
                                <TableRow key={sample.id}>
                                    <TableCell>{sample.sampleId}</TableCell>
                                    <TableCell>{sample.clientName}</TableCell>
                                    <TableCell>{sample.details}</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={sample.sampleStatus || ''}
                                                onChange={(e) => handleStatusChange(sample.id, e.target.value)}
                                            >
                                                <MenuItem value="Yet to receive from Vendor">Yet to receive from Vendor</MenuItem>
                                                <MenuItem value="At eyantra Location">At eyantra Location</MenuItem>
                                                <MenuItem value="With Client">With Client</MenuItem>
                                                <MenuItem value="Sent back to vendor">Sent back to vendor</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="center">
                                        {sample.statusUpdatedAt ? differenceInDays(new Date(), new Date(sample.statusUpdatedAt)) : 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 250 }}>
                                        <TextField
                                            size="small"
                                            variant="outlined"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            value={remarks[sample.id] || ''}
                                            onChange={(e) => handleRemarksChange(sample.id, e.target.value)}
                                            onBlur={() => handleRemarksBlur(sample.id)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow><TableCell colSpan={6} align="center">No samples found.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </Box>
    );
};

export default SampleDashboard;